import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext(null);

const themes = {
  dark: {
    background: '#060d18',
    cardBg: '#0f1a2a',
    cardAlt: '#0f1a2a',
    softAccentBg: 'rgba(99,102,241,0.12)',
    primaryText: '#E5E7EB',
    secondaryText: '#9CA3AF',
    mutedTextColor: '#6B7280',
    sectionHeading: '#9CA3AF',
    accent: '#6366F1',
    accentHover: '#4F46E5',
    accentMuted: 'rgba(99,102,241,0.10)',
    accentBorder: 'rgba(99,102,241,0.35)',
    profit: '#16C784',
    loss: '#EF4444',
    warning: '#F59E0B',
    info: '#3B82F6',
    border: 'rgba(255,255,255,0.08)',
    borderSoft: 'rgba(255,255,255,0.05)',
    cardHeaderBg: 'transparent',
    cardHeaderBorder: 'rgba(255,255,255,0.06)',
    inputBg: '#162030',
    inputBorder: '#374151',
    cardShadow: '0 4px 24px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04)',
    accentShadow: '0 10px 30px rgba(99,102,241,0.15)',
    skeletonBg: 'rgba(255,255,255,0.08)',
    skeletonBgAlt: 'rgba(255,255,255,0.06)',
    skeletonBgSubtle: 'rgba(255,255,255,0.05)',
    divider: 'rgba(255,255,255,0.06)',
    dividerMed: 'rgba(255,255,255,0.07)',
    trackBg: 'rgba(255,255,255,0.07)',
    overlayBg: 'rgba(0,0,0,0.72)',
    cardBorder: 'rgba(255,255,255,0.07)',
    cardGradient: 'linear-gradient(180deg, rgba(255,255,255,0.025) 0%, transparent 60%)',
    ringTrack: 'rgba(255,255,255,0.07)',
    ringTrackAlt: 'rgba(255,255,255,0.08)',
    mutedText: 'rgba(255,255,255,0.3)',
    mutedTextSoft: 'rgba(255,255,255,0.2)',
    mutedTextStrong: 'rgba(255,255,255,0.4)',
    mutedTextXS: 'rgba(255,255,255,0.28)',
    inactiveBg: 'rgba(255,255,255,0.02)',
    inactiveBgAlt: 'rgba(255,255,255,0.03)',
    inactiveBgStrong: 'rgba(255,255,255,0.05)',
    inactiveBorder: 'rgba(255,255,255,0.06)',
    inactiveBorderAlt: 'rgba(255,255,255,0.08)',
    inactiveBorderStrong: 'rgba(255,255,255,0.1)',
    progressTrack: 'rgba(255,255,255,0.06)',
  },
  light: {
    background: '#F4F7FB',
    cardBg: '#FFFFFF',
    cardAlt: '#F8FAFC',
    softAccentBg: '#EEF2FF',
    primaryText: '#111827',
    secondaryText: '#6B7280',
    mutedTextColor: '#94A3B8',
    sectionHeading: '#475569',
    accent: '#6366F1',
    accentHover: '#4F46E5',
    accentMuted: 'rgba(99,102,241,0.10)',
    accentBorder: '#C7D2FE',
    profit: '#22C55E',
    loss: '#EF4444',
    warning: '#F59E0B',
    info: '#3B82F6',
    border: '#E5E7EB',
    borderSoft: '#EDF2F7',
    cardHeaderBg: '#EEF2FF',
    cardHeaderBorder: '#C7D2FE',
    inputBg: '#F3F4F6',
    inputBorder: '#D1D5DB',
    cardShadow: '0 8px 24px rgba(15,23,42,0.06)',
    accentShadow: '0 10px 30px rgba(99,102,241,0.10)',
    skeletonBg: '#E5E7EB',
    skeletonBgAlt: '#EDEFF2',
    skeletonBgSubtle: '#F3F4F6',
    divider: '#E5E7EB',
    dividerMed: '#D1D5DB',
    trackBg: '#E5E7EB',
    overlayBg: 'rgba(15,23,42,0.55)',
    cardBorder: '#E5E7EB',
    cardGradient: 'linear-gradient(180deg, rgba(255,255,255,0.6) 0%, transparent 60%)',
    ringTrack: '#E5E7EB',
    ringTrackAlt: '#EDEFF2',
    mutedText: '#94A3B8',
    mutedTextSoft: '#D1D5DB',
    mutedTextStrong: '#6B7280',
    mutedTextXS: '#94A3B8',
    inactiveBg: '#F9FAFB',
    inactiveBgAlt: '#F3F4F6',
    inactiveBgStrong: '#EDEFF2',
    inactiveBorder: '#E5E7EB',
    inactiveBorderAlt: '#D1D5DB',
    inactiveBorderStrong: '#C9D0DA',
    progressTrack: '#E5E7EB',
  },
};

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('tradelab_theme');
    return saved || 'dark'; // Dark is default
  });

  useEffect(() => {
    localStorage.setItem('tradelab_theme', theme);
    document.documentElement.style.setProperty('--bg-primary', themes[theme].background);
    document.documentElement.style.setProperty('--bg-card', themes[theme].cardBg);
    document.documentElement.style.setProperty('--text-primary', themes[theme].primaryText);
    document.documentElement.style.setProperty('--text-secondary', themes[theme].secondaryText);
    document.documentElement.style.setProperty('--color-accent', themes[theme].accent);
    document.documentElement.style.setProperty('--color-profit', themes[theme].profit);
    document.documentElement.style.setProperty('--color-loss', themes[theme].loss);
    document.documentElement.style.setProperty('--border-color', themes[theme].border);
    document.documentElement.style.setProperty('--card-shadow', themes[theme].cardShadow);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  const colors = themes[theme];

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, colors }}>
      <div style={{ backgroundColor: colors.background, color: colors.primaryText, minHeight: '100vh' }}>
        {children}
      </div>
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}
